import React, { useState, useEffect } from 'react';
import { CalendarDays, Plus, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { saasApi } from '../../api/client';
import { useToast } from '../../hooks/use-toast';

export const AgendaTab = () => {
  const [appts, setAppts] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ contactId: '', startsAt: '', notes: '' });
  const { toast } = useToast();

  const load = () => {
    saasApi.appointments().then(setAppts).catch(() => setAppts([]));
    saasApi.crmContacts().then(setContacts).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (appts === null) return <div className="text-[#52676e] text-sm py-8 text-center">Carregando agenda...</div>;

  const create = async () => {
    if (!form.contactId || !form.startsAt) {
      toast({ title: 'Preencha contato e data', variant: 'destructive' });
      return;
    }
    try {
      await saasApi.createAppointment({ ...form, startsAt: new Date(form.startsAt).toISOString() });
      setOpen(false);
      setForm({ contactId: '', startsAt: '', notes: '' });
      toast({ title: 'Agendamento criado!' });
      load();
    } catch (err) {
      toast({ title: 'Erro', description: err.response?.data?.detail || 'Falha ao agendar.', variant: 'destructive' });
    }
  };

  const cancel = async (id) => {
    await saasApi.cancelAppointment(id);
    toast({ title: 'Agendamento cancelado' });
    load();
  };

  return (
    <div className="space-y-4" data-testid="agenda-tab">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" className="bg-[#153e44] hover:bg-[#0c3036]" data-testid="agenda-new-btn"><Plus className="w-4 h-4 mr-1.5" /> Novo agendamento</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Novo agendamento</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider mb-1.5 block">Contato do CRM</label>
                <Select value={form.contactId} onValueChange={v => setForm({ ...form, contactId: v })}>
                  <SelectTrigger data-testid="agenda-contact-select"><SelectValue placeholder="Selecione um contato" /></SelectTrigger>
                  <SelectContent>
                    {contacts.length === 0 && <SelectItem value="none" disabled>Nenhum contato no CRM</SelectItem>}
                    {contacts.map(c => <SelectItem key={c.id} value={c.id}>{c.name} — {c.category}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider mb-1.5 block">Data e hora</label>
                <Input type="datetime-local" value={form.startsAt} onChange={e => setForm({ ...form, startsAt: e.target.value })} data-testid="agenda-datetime-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider mb-1.5 block">Observações</label>
                <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Ex: videochamada, levar documentos..." />
              </div>
              <Button onClick={create} className="w-full bg-[#153e44] hover:bg-[#0c3036]" data-testid="agenda-create-btn">Agendar</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {appts.length === 0 ? (
        <div className="bg-white border border-[#cbd8dc] rounded-[10px] p-10 text-center" data-testid="agenda-empty">
          <CalendarDays className="w-10 h-10 text-[#6b858e] mx-auto mb-3" />
          <h3 className="font-semibold text-[#153e44]">Nenhum agendamento</h3>
          <p className="text-sm text-[#52676e] mt-1">Crie agendamentos a partir dos contatos do CRM.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {appts.map(a => (
            <div key={a.id} className={`bg-white border border-[#cbd8dc] rounded-[7px] p-4 flex flex-wrap items-center justify-between gap-3 ${a.status === 'cancelado' ? 'opacity-60' : ''}`} data-testid={`agenda-item-${a.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-[7px] bg-[#153e44] flex items-center justify-center"><CalendarDays className="w-5 h-5 text-[#075a6d]" /></div>
                <div>
                  <div className="font-semibold text-[#153e44] text-sm">{a.contactName}</div>
                  <div className="text-xs text-[#52676e]">{new Date(a.startsAt).toLocaleString('pt-BR')} {a.notes && `• ${a.notes}`}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${a.status === 'agendado' ? 'bg-emerald-100 text-emerald-800' : 'bg-[#f2f6f7] text-[#52676e]'}`}>{a.status === 'agendado' ? 'Agendado' : 'Cancelado'}</span>
                {a.status === 'agendado' && <Button size="sm" variant="ghost" onClick={() => cancel(a.id)} className="text-red-600 hover:bg-red-50 h-8" data-testid={`agenda-cancel-${a.id}`}><XCircle className="w-4 h-4" /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
